import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw } from 'lucide-react';
import api from '@/lib/api';

export const ErpHealthIndicator = ({ className = '' }) => {
  const [erpHealth, setErpHealth] = useState({
    status: 'CHECKING', // 'CHECKING' | 'UP' | 'DOWN'
    reachable: false,
    timestamp: null,
    companyId: null,
  });
  const [checkingHealth, setCheckingHealth] = useState(false);

  const checkErpHealth = useCallback(async () => {
    try {
      setCheckingHealth(true);
      const res = await api.get('/erp/health');
      const data = res.data?.data || res.data;
      const isUp = data?.status === 'UP' || data?.reachable === true;
      setErpHealth({
        status: isUp ? 'UP' : 'DOWN',
        reachable: !!isUp,
        timestamp: data?.timestamp || new Date().toISOString(),
        companyId: data?.companyId || null,
      });
    } catch (err) {
      setErpHealth({
        status: 'DOWN',
        reachable: false,
        timestamp: new Date().toISOString(),
        companyId: null,
      });
    } finally {
      setCheckingHealth(false);
    }
  }, []);

  useEffect(() => {
    checkErpHealth();

    const handleHealthCheckEvent = () => {
      checkErpHealth();
    };
    window.addEventListener('erp:health-check', handleHealthCheckEvent);
    return () => {
      window.removeEventListener('erp:health-check', handleHealthCheckEvent);
    };
  }, [checkErpHealth]);

  return (
    <div className={`w-full ${className}`}>
      {erpHealth.status === 'CHECKING' && (
        <div
          data-testid="erp-health-indicator"
          data-status="CHECKING"
          className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-xs font-medium bg-muted text-muted-foreground border border-border"
          title="Checking OneSpace ERP reachability..."
        >
          <div className="flex items-center gap-2 min-w-0">
            <RefreshCw className="h-3.5 w-3.5 animate-spin flex-shrink-0 text-muted-foreground" />
            <span className="truncate">ERP: Checking...</span>
          </div>
        </div>
      )}
      {erpHealth.status === 'UP' && (
        <div
          data-testid="erp-health-indicator"
          data-status="UP"
          onClick={checkErpHealth}
          className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-xs font-medium bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800 cursor-pointer hover:bg-emerald-100/60 transition-colors"
          title={
            erpHealth.companyId
              ? `Connected to OneSpace ERP (Company #${erpHealth.companyId}). Click to re-check.`
              : 'Connected to OneSpace ERP. Click to re-check.'
          }
        >
          <div className="flex items-center gap-2 min-w-0">
            <span className="relative flex h-2 w-2 flex-shrink-0">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
            </span>
            <span className="truncate">ERP: Connected</span>
          </div>
          <RefreshCw
            className={`h-3 w-3 text-emerald-600 opacity-60 hover:opacity-100 ${checkingHealth ? 'animate-spin' : ''
              }`}
          />
        </div>
      )}
      {erpHealth.status === 'DOWN' && (
        <div
          data-testid="erp-health-indicator"
          data-status="DOWN"
          onClick={checkErpHealth}
          className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-xs font-medium bg-rose-50 dark:bg-rose-950/40 text-rose-700 dark:text-rose-400 border border-rose-200 dark:border-rose-800 cursor-pointer hover:bg-rose-100/60 transition-colors"
          title="ERP connection is unavailable. Click to retry."
        >
          <div className="flex items-center gap-2 min-w-0">
            <span className="inline-flex rounded-full h-2 w-2 bg-rose-500 flex-shrink-0"></span>
            <span className="truncate">ERP: Disconnected</span>
          </div>
          <RefreshCw
            className={`h-3 w-3 text-rose-600 opacity-60 hover:opacity-100 ${checkingHealth ? 'animate-spin' : ''
              }`}
          />
        </div>
      )}
    </div>
  );
};

export default ErpHealthIndicator;

